/* eslint-disable react/prop-types */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Button,
  Tooltip,
} from "@material-ui/core";
import ScenarioNameComponent from "app/scenarios/map/constraints/ScenarioNameComponent";
import React, { useEffect, useState } from "react";
import _ from "lodash";
import buildRequestUrl from "lib/requests/requestUtils";
import athenaClient from "lib/axios/clients";
import { useSelector } from "react-redux";
import mixpanelTrack from "app/common/MixpanelComponent";
import CompetitorFilters from "./CompetitorFilters";
import { getAsyncCustomCompetitorData } from "../utils/getAsyncCompetitorsScenariosBaseData";
import getAsyncCompetitorsScenariosData from "../utils/getAsyncCompetitorsScenariosData";
import CompetitorSavingDialog from "./CompetitorSavingDialog";
import CompetitorListComponent from "./CompetitorListComponent";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    borderBottom: theme.box.border,
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  dialogContent: {
    padding: "15px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    gap: "10px",
  },
  scenarioNameContainer: {
    height: "125px",
    border: "1px solid #e4e4e4",
    borderRadius: "5px",
    display: "flex",
    width: "98%",
  },
  scenarioEditContainer: {
    width: "98%",
    height: "585px",
    border: "1px solid #e4e4e4",
    borderRadius: "5px",
    display: "flex",
    "@media (max-height: 800px)": {
      height: "400px",
    },
  },
  scenarioSlidersContainer: {
    width: "70%",
    overflow: "auto",
  },
  dialogActions: {
    borderTop: theme.box.border,
  },
  saveTooltip: { fontSize: "30px" },
}));
export default function CompetitorDialog({
  isActive = false,
  baseCompetitorScenarioData,
  competitorData,
  setCompetitorsData,
  handleDialogClose,
  setBaseCompetitorKpis,
}) {
  const classes = useStyles();
  const status = {
    noSaving: "",
    loading: "Loading",
    ok: "Ok",
    error: "Error",
    timeout: "Timeout",
  };
  const defaultInputName =
    competitorData.scenarioData === null
      ? ""
      : competitorData.scenarioData.name;
  const defaultinputComment =
    competitorData.scenarioData === null
      ? ""
      : competitorData.scenarioData.comment;
  const defaultFilters = {
    category: "",
    company: "",
    brand: "",
    groupBy: {
      key: "category-company",
      label: "Category-Company",
    },
  };

  const [inputName, setInputName] = useState(defaultInputName);
  const [inputComment, setInputComment] = useState(defaultinputComment);
  const [isInvalidCharacterInName, setIsInvalidCharacterInName] = useState(
    false
  );
  const [
    isInvalidCharacterInComment,
    setIsInvalidCharacterInComment,
  ] = useState(false);
  const [competitorFilters, setCompetitorFilters] = useState(defaultFilters);
  const [slidersData, setSlidersData] = useState(
    _.cloneDeep(competitorData.slidersData)
  );
  const [savingStatus, setSavingStatus] = useState(status.noSaving);
  const authSlice = useSelector((state) => state.auth);
  const cyclesListSlice = useSelector((state) => state.cycle.type);
  const {
    features: { IsCompetitors2_1: isCompetitors2point1 },
  } = useSelector((state) => state.auth.configCatUser);

  const handleCancelButton = () => {
    setInputName(defaultInputName);
    setInputComment(defaultinputComment);
    setCompetitorFilters(defaultFilters);
    setSlidersData(_.cloneDeep(competitorData.slidersData));
    handleDialogClose();
  };

  const handleSaveScenario = async () => {
    try {
      const sliderPrices = [];
      _.forEach(slidersData, (category) => {
        _.forEach(category.companies, (company) => {
          if (competitorFilters.groupBy.key === "category-company") {
            _.forEach(company.products, (product) => {
              sliderPrices.push({
                price_percentage: Math.abs(company.sliderValue / 100),
                price_direction:
                  company.sliderValue < 0 ? "decrease" : "increase",
                product_id: product.id,
              });
            });
          } else {
            _.forEach(company.brands, (brand) => {
              _.forEach(brand.products, (product) => {
                sliderPrices.push({
                  price_percentage: Math.abs(brand.sliderValue / 100),
                  price_direction:
                    brand.sliderValue < 0 ? "decrease" : "increase",
                  product_id: product.id,
                });
              });
            });
          }
        });
      });
      setSavingStatus(status.loading);
      const competitorBaseData = await getAsyncCustomCompetitorData({
        scenarioId: baseCompetitorScenarioData.scenarioId,
        sliderPrices,
        name: inputName,
        comment: inputComment,
      });
      const scenarioData = await getAsyncCompetitorsScenariosData(
        [competitorBaseData],
        cyclesListSlice,
        baseCompetitorScenarioData,
        authSlice.user?.username
      );

      const configCompetitorsScenario = {
        method: "PUT",
        url: buildRequestUrl({}, "competitors/config"),
        data: {
          scenario_base_id: baseCompetitorScenarioData.scenarioId,
          competitors_id: competitorData.id,
          slider_prices: sliderPrices,
          name: inputName,
          comment: inputComment,
        },
      };
      await athenaClient(configCompetitorsScenario);

      setCompetitorsData((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState[competitorData.id] = {
          ...prevState[competitorData.id],
          scenarioData: scenarioData[0],
          slidersData,
        };
        return newState;
      });
      setBaseCompetitorKpis(scenarioData[0]);

      setSavingStatus(status.ok);
      /* MIXPANEL TRACKS */
      mixpanelTrack("Competitor scenario created or modified");
      /* END OF MIXPANEL TRACKS */
      handleDialogClose();
    } catch (error) {
      setSavingStatus(status.error);
    }
  };

  useEffect(() => {
    if (savingStatus === status.ok) {
      setSavingStatus(status.noSaving);
    }
  }, [savingStatus]);

  useEffect(() => {
    const inputNameWithoutSpaces = inputName.trimStart().replace("  ", " ");
    if (inputNameWithoutSpaces !== inputName) {
      setInputName(inputNameWithoutSpaces);
    }
    setIsInvalidCharacterInName(
      !/^[A-Za-z0-9\sÁÉÍÓÚáéíóúÑñÜü-]+$/.test(inputName.trim()) &&
        inputName !== ""
    );
  }, [inputName]);

  useEffect(() => {
    if (inputComment)
      setIsInvalidCharacterInComment(
        inputComment.trim().includes("|") || inputComment.trim().includes(",")
      );
  }, [inputComment]);

  useEffect(() => {
    setSlidersData(competitorData.slidersData);
  }, [competitorFilters.groupBy.key]);

  return (
    <>
      <CompetitorSavingDialog
        scenarioName={inputName}
        actualStatus={savingStatus}
        setSavingStatus={setSavingStatus}
      />

      <Dialog open={isActive} maxWidth="lg" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          New Competitor Scenario
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.scenarioNameContainer}>
            <ScenarioNameComponent
              newScenarioName={inputName}
              setNewScenarioName={setInputName}
              newScenarioComment={inputComment}
              setNewScenarioComment={setInputComment}
              invalidName={false}
              invalidcharacter={isInvalidCharacterInName}
              invalidcharacterInCommment={isInvalidCharacterInComment}
              isCompetitors
            />
          </Box>
          <Box className={classes.scenarioEditContainer}>
            <CompetitorFilters
              sliderDataByCategories={slidersData}
              competitorsFilters={competitorFilters}
              setCompetitorFilters={setCompetitorFilters}
              groupByEnabled={isCompetitors2point1}
            />

            <Box className={classes.scenarioSlidersContainer}>
              <CompetitorListComponent
                slidersData={slidersData}
                setSlidersData={setSlidersData}
                competitorsFilters={competitorFilters}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button autoFocus onClick={handleCancelButton}>
            Close
          </Button>
          <Tooltip
            className={classes.saveTooltip}
            title={
              isInvalidCharacterInName ||
              isInvalidCharacterInComment ||
              inputName === "" ? (
                <h2>Please add a valid name to the scenario</h2>
              ) : (
                ""
              )
            }
          >
            <div>
              <Button
                autoFocus
                onClick={handleSaveScenario}
                variant="contained"
                color="primary"
                disabled={
                  isInvalidCharacterInName ||
                  isInvalidCharacterInComment ||
                  inputName === ""
                }
              >
                Save
              </Button>
            </div>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
}
